import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Bluetooth, Info, Loader2, Plus, Printer, RefreshCw, Trash2 } from 'lucide-react';
import {
  createPrinter,
  deactivateDevice,
  deletePrinter,
  registerDevice,
  testPosPrinter,
  updateDevice,
  updatePrinter,
  waitForPrintJob,
} from '../lib/api';
import { clearDeviceIdentity, describePlatform, writeDeviceIdentity } from '../lib/deviceIdentity';
import { usePrintJobs } from '../contexts/PrintJobContext';
import { PRINTER_TYPE_OPTIONS, printerNeedsAgent, printerTypeLabel } from '../lib/printTransports';
import { AGENT_URL } from '../lib/printTransports/agent';
import PrinterDiscovery from '../components/PrinterDiscovery';
import { isWebBluetoothSupported, pairBluetoothPrinter } from '../lib/printTransports/webBluetooth';
import { DetailCard, KeyValueRows, SectionHeader, SelectField, StatusBadge } from './settingsUi';

const OVERRIDE_TARGETS = [
  { id: 'receipt', label: 'Kassenbon', subtitle: 'Bon nach dem Bezahlen an diesem Gerät' },
  { id: 'invoice', label: 'Rechnung', subtitle: 'Rechnungskopie von diesem Gerät' },
  { id: 'report', label: 'Bericht', subtitle: 'Berichte, die an diesem Gerät gedruckt werden' },
];
const PAPER_OPTIONS = [
  { value: '58', label: '58 mm' },
  { value: '80', label: '80 mm' },
  { value: '210', label: 'A4' },
];
const EMPTY_FORM = { id: null, name: '', type: 'network', connection_string: '', paper_width: 80, is_default: false };
const CONNECTION_PLACEHOLDERS = {
  network: '192.168.1.50:9100',
  system: 'Name der Warteschlange, z. B. Printer_printer_80',
  serial: '/dev/cu.BLTH',
  epson_epos: '192.168.1.60',
  bluetooth: 'Über „Koppeln“ auswählen',
};

const BUTTON = 'inline-flex h-11 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60';
const PRIMARY_BUTTON = 'inline-flex h-11 items-center justify-center bg-primary px-4 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60';
const DANGER_BUTTON = 'inline-flex h-11 items-center justify-center border border-red-200 bg-card px-4 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60';
const INPUT = 'w-full border border-border bg-card px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-primary';

function errorMessage(error, fallback) {
  return error?.response?.data?.detail || error?.message || fallback;
}

function Hint({ children }) {
  return (
    <div className="flex items-start gap-3 text-sm text-muted-foreground">
      <Info className="mt-0.5 h-5 w-5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function PrinterForm({ form, setForm, busy, onSave, onCancel, onPairBluetooth }) {
  const typeOption = PRINTER_TYPE_OPTIONS.find((option) => option.value === form.type);
  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  return (
    <DetailCard title={form.id ? 'Drucker bearbeiten' : 'Drucker hinzufügen'} subtitle={typeOption?.hint}>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1 text-sm font-medium text-foreground">
          <span>Art</span>
          <SelectField
            value={form.type}
            options={PRINTER_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
            onChange={(value) => update({ type: value, connection_string: '' })}
          />
        </label>
        <label className="space-y-1 text-sm font-medium text-foreground">
          <span>Name</span>
          <input className={INPUT} value={form.name} onChange={(event) => update({ name: event.target.value })} placeholder="z. B. Bondrucker Theke" />
        </label>
        <label className="space-y-1 text-sm font-medium text-foreground md:col-span-2">
          <span>Verbindung</span>
          <div className="flex gap-2">
            <input
              className={`${INPUT} font-mono`}
              value={form.connection_string}
              onChange={(event) => update({ connection_string: event.target.value })}
              placeholder={CONNECTION_PLACEHOLDERS[form.type]}
              readOnly={form.type === 'bluetooth'}
            />
            {form.type === 'bluetooth' ? (
              <button type="button" className={BUTTON} onClick={onPairBluetooth} disabled={!isWebBluetoothSupported()}>
                <Bluetooth className="mr-2 h-4 w-4" />
                Koppeln
              </button>
            ) : null}
          </div>
        </label>
        <label className="space-y-1 text-sm font-medium text-foreground">
          <span>Papierbreite</span>
          <SelectField
            value={String(form.paper_width)}
            options={PAPER_OPTIONS}
            onChange={(value) => update({ paper_width: parseInt(value, 10) })}
          />
        </label>
        <label className="flex items-center gap-3 self-end pb-2 text-sm font-medium text-foreground">
          <input type="checkbox" checked={form.is_default} onChange={(event) => update({ is_default: event.target.checked })} />
          Standarddrucker der Firma
        </label>
      </div>

      {form.type === 'bluetooth' && !isWebBluetoothSupported() ? (
        <p className="mt-4 text-sm text-amber-700">
          Dieser Browser kann kein Bluetooth. Mobile Bluetooth-Drucker funktionieren mit Chrome auf Android (nicht mit iPhone/Safari).
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2">
        <button type="button" className={PRIMARY_BUTTON} onClick={onSave} disabled={busy === 'save-printer'}>
          {busy === 'save-printer' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Speichern
        </button>
        <button type="button" className={BUTTON} onClick={onCancel}>Abbrechen</button>
      </div>
    </DetailCard>
  );
}

export default function DeviceSettingsPanel({ isManager, devicesUnavailable, printerOptions, onChanged }) {
  const {
    identity,
    device,
    printers,
    deviceRevoked,
    needsAgent,
    agentHealth,
    agentChecked,
    recentJobs,
    checkAgent,
    refreshDevice,
  } = usePrintJobs();
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setDeviceName(device?.name || identity?.name || '');
  }, [device?.name, identity?.name]);

  const overrides = device?.printer_overrides || {};
  const overrideOptions = useMemo(
    () => [{ value: '', label: 'Firmen-Standard verwenden' }, ...(printerOptions || [])],
    [printerOptions],
  );
  const showAgentCard = needsAgent || (form && printerNeedsAgent(form));

  const run = async (key, action, fallbackError) => {
    setBusy(key);
    try {
      return await action();
    } catch (error) {
      toast.error(errorMessage(error, fallbackError));
      return undefined;
    } finally {
      setBusy('');
    }
  };

  const reloadAll = async () => {
    await refreshDevice();
    await onChanged?.();
  };

  const handleRegister = () => run('register', async () => {
    const name = deviceName.trim();
    if (!name) {
      toast.error('Bitte einen Gerätenamen eingeben');
      return;
    }
    const result = await registerDevice({
      name,
      platform: describePlatform(),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
    writeDeviceIdentity({ id: result.device.id, name: result.device.name, token: result.device_token });
    toast.success(`Gerät „${result.device.name}“ registriert`);
    await onChanged?.();
  }, 'Gerät konnte nicht registriert werden');

  const handleRename = () => run('rename', async () => {
    const updated = await updateDevice(identity.id, { name: deviceName.trim() });
    writeDeviceIdentity({ ...identity, name: updated.name });
    toast.success('Gerätename gespeichert');
    await reloadAll();
  }, 'Name konnte nicht gespeichert werden');

  const handleRemove = () => {
    if (!window.confirm('Dieses Gerät und alle seine Drucker entfernen?')) return;
    run('remove', async () => {
      await deactivateDevice(identity.id);
      clearDeviceIdentity();
      toast.success('Gerät entfernt');
      await onChanged?.();
    }, 'Gerät konnte nicht entfernt werden');
  };

  // A found printer becomes a pre-filled form; the user only confirms with "Speichern".
  const handleCandidate = (values) => {
    setAdding(false);
    setForm({ ...EMPTY_FORM, ...values, is_default: printers.length === 0 });
  };

  const handlePairBluetooth = async () => {
    try {
      const paired = await pairBluetoothPrinter();
      setForm((current) => ({ ...current, connection_string: paired.id, name: current.name || paired.name }));
    } catch (error) {
      if (error?.name !== 'NotFoundError') toast.error(error.message); // NotFoundError = picker cancelled
    }
  };

  const handleReconnectBluetooth = (printer) => run(`connect:${printer.id}`, async () => {
    const paired = await pairBluetoothPrinter();
    if (paired.id !== printer.connection_string) {
      await updatePrinter(printer.id, { ...printer, connection_string: paired.id, device_id: identity.id });
      await refreshDevice();
    }
    toast.success(`${printer.name} verbunden`);
  }, 'Bluetooth-Drucker konnte nicht verbunden werden');

  const handleSavePrinter = () => run('save-printer', async () => {
    if (!form.name.trim() || !form.connection_string.trim()) {
      toast.error('Bitte Name und Verbindung angeben');
      return;
    }
    const payload = {
      name: form.name.trim(),
      type: form.type,
      connection_string: form.connection_string.trim(),
      paper_width: form.paper_width,
      is_default: form.is_default,
      device_id: identity.id,
    };
    if (form.id) {
      await updatePrinter(form.id, payload);
    } else {
      await createPrinter(payload);
    }
    toast.success(form.id ? 'Drucker gespeichert' : 'Drucker hinzugefügt');
    setForm(null);
    await reloadAll();
  }, 'Drucker konnte nicht gespeichert werden');

  const handleDeletePrinter = (printer) => {
    if (!window.confirm(`Drucker „${printer.name}“ löschen?`)) return;
    run(`delete:${printer.id}`, async () => {
      await deletePrinter(printer.id);
      toast.success('Drucker gelöscht');
      await reloadAll();
    }, 'Drucker konnte nicht gelöscht werden');
  };

  const handleTest = (printer) => run(`test:${printer.id}`, async () => {
    const result = await testPosPrinter(printer.id);
    if (!result?.job_id) {
      toast.success(result?.message || 'Testseite gesendet');
      return;
    }
    const job = await waitForPrintJob(result.job_id);
    if (job?.status === 'done') {
      toast.success(`Testseite auf ${printer.name} gedruckt`);
    } else if (job?.status === 'failed') {
      toast.error(`${printer.name}: ${job.error || 'Druck fehlgeschlagen'}`);
    } else {
      toast.warning('Noch keine Rückmeldung vom Gerät. Ist die Kasse mit diesem Drucker geöffnet?');
    }
  }, 'Testdruck fehlgeschlagen');

  const handleOverride = (target, printerId) => run(`override:${target}`, async () => {
    await updateDevice(identity.id, { printer_overrides: { ...overrides, [target]: printerId || null } });
    await refreshDevice();
    toast.success('Zuordnung für dieses Gerät gespeichert');
  }, 'Zuordnung konnte nicht gespeichert werden');

  if (devicesUnavailable) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <SectionHeader title="Dieses Gerät" subtitle="Drucker werden immer an dem Gerät eingerichtet, an dem sie hängen." />
        <div className="flex-1 overflow-y-auto p-5">
          <DetailCard title="Geräteverwaltung nicht verfügbar" subtitle="Die Datenbank ist noch nicht vorbereitet.">
            <Hint>Bitte die Migration <span className="font-mono">19_devices_and_print_jobs.sql</span> im Supabase-Dashboard ausführen.</Hint>
          </DetailCard>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader
        title="Dieses Gerät"
        subtitle="Drucker werden immer an dem Gerät eingerichtet, an dem sie hängen. Dieses Gerät druckt seine Aufträge selbst."
      />

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {!identity ? (
          <DetailCard title="Gerät registrieren" subtitle="Einmalig pro Kasse, PC oder Handy.">
            <Hint>
              Erst ein registriertes Gerät kann eigene Drucker haben und Druckaufträge ausführen, auch wenn gerade niemand angemeldet ist.
            </Hint>
            {isManager ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <input className={`${INPUT} max-w-sm`} value={deviceName} onChange={(event) => setDeviceName(event.target.value)} placeholder="z. B. Kasse 1, Bar-PC, Handy Anna" />
                <button type="button" className={PRIMARY_BUTTON} onClick={handleRegister} disabled={busy === 'register'}>
                  {busy === 'register' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Dieses Gerät registrieren
                </button>
              </div>
            ) : (
              <p className="mt-4 text-sm font-semibold text-amber-700">Zum Registrieren bitte mit einem Manager- oder Admin-PIN anmelden.</p>
            )}
          </DetailCard>
        ) : deviceRevoked ? (
          <DetailCard title="Gerät nicht mehr registriert" subtitle="Das Gerät wurde entfernt oder deaktiviert.">
            <Hint>Dieses Gerät holt keine Druckaufträge mehr ab. Registrierung entfernen und das Gerät neu registrieren.</Hint>
            <button type="button" className={`${BUTTON} mt-4`} onClick={clearDeviceIdentity}>Registrierung entfernen</button>
          </DetailCard>
        ) : (
          <>
            <DetailCard title={device?.name || identity.name || 'Dieses Gerät'} subtitle="Registriert – holt seine Druckaufträge alle 2 Sekunden ab.">
              <KeyValueRows
                rows={[
                  { label: 'Status', value: <StatusBadge tone={device?.online ? 'positive' : 'warning'}>{device?.online ? 'Online' : 'Verbindet'}</StatusBadge> },
                  { label: 'Plattform', value: device?.platform || describePlatform() },
                  { label: 'Geräte-ID', value: <span className="font-mono">{identity.id.slice(0, 8)}</span> },
                ]}
              />
              {isManager ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  <input className={`${INPUT} max-w-sm`} value={deviceName} onChange={(event) => setDeviceName(event.target.value)} />
                  <button type="button" className={BUTTON} onClick={handleRename} disabled={busy === 'rename' || !deviceName.trim()}>Umbenennen</button>
                  <button type="button" className={DANGER_BUTTON} onClick={handleRemove} disabled={busy === 'remove'}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Gerät entfernen
                  </button>
                </div>
              ) : null}
            </DetailCard>

            <DetailCard title="Drucker dieses Geräts" subtitle="Netzwerk-, USB-, Epson- und Bluetooth-Drucker, die an diesem Gerät hängen.">
              {printers.length === 0 ? (
                <Hint>Noch kein Drucker an diesem Gerät. {isManager ? 'Mit „Drucker hinzufügen“ einrichten.' : 'Ein Manager kann hier Drucker einrichten.'}</Hint>
              ) : (
                <div className="divide-y divide-slate-100 border border-border">
                  {printers.map((printer) => (
                    <div key={printer.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base font-semibold text-foreground">{printer.name}</p>
                          {printer.is_default ? <StatusBadge tone="info">Standard</StatusBadge> : null}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {printerTypeLabel(printer.type)} · <span className="font-mono">{printer.connection_string}</span> · {Number(printer.paper_width) >= 200 ? 'A4' : `${printer.paper_width} mm`}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {printer.type === 'bluetooth' ? (
                          <button type="button" className={BUTTON} onClick={() => handleReconnectBluetooth(printer)} disabled={busy === `connect:${printer.id}`}>
                            <Bluetooth className="mr-2 h-4 w-4" />
                            Verbinden
                          </button>
                        ) : null}
                        <button type="button" className={BUTTON} onClick={() => handleTest(printer)} disabled={busy === `test:${printer.id}`}>
                          {busy === `test:${printer.id}` ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
                          Testdruck
                        </button>
                        {isManager ? (
                          <>
                            <button
                              type="button"
                              className={BUTTON}
                              onClick={() => {
                                setAdding(false);
                                setForm({ ...EMPTY_FORM, ...printer, paper_width: Number(printer.paper_width) || 80, is_default: Boolean(printer.is_default) });
                              }}
                            >
                              Bearbeiten
                            </button>
                            <button type="button" className={DANGER_BUTTON} onClick={() => handleDeletePrinter(printer)} disabled={busy === `delete:${printer.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {isManager && !form && (adding || printers.length === 0) ? (
                <div className="mt-4 space-y-3">
                  <PrinterDiscovery existingPrinters={printers} onSelect={handleCandidate} />
                  <button
                    type="button"
                    className={BUTTON}
                    onClick={() => {
                      setAdding(false);
                      setForm({ ...EMPTY_FORM, is_default: printers.length === 0 });
                    }}
                  >
                    Manuell eingeben
                  </button>
                </div>
              ) : null}
              {isManager && !form && !adding && printers.length > 0 ? (
                <button type="button" className={`${PRIMARY_BUTTON} mt-4`} onClick={() => setAdding(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Drucker hinzufügen
                </button>
              ) : null}
            </DetailCard>

            {form ? (
              <PrinterForm
                form={form}
                setForm={setForm}
                busy={busy}
                onSave={handleSavePrinter}
                onCancel={() => setForm(null)}
                onPairBluetooth={handlePairBluetooth}
              />
            ) : null}

            {showAgentCard ? (
              <DetailCard title="Druck-Agent" subtitle="Leitet Aufträge an Netzwerk- und USB-Drucker weiter. Muss auf diesem Gerät laufen.">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <StatusBadge tone={agentHealth ? 'positive' : agentChecked ? 'danger' : 'neutral'}>
                      {agentHealth ? 'Läuft' : agentChecked ? 'Nicht erreichbar' : 'Wird geprüft'}
                    </StatusBadge>
                    <span className="text-sm text-muted-foreground">
                      {agentHealth ? `Version ${agentHealth.version} · ${agentHealth.platform}` : AGENT_URL}
                    </span>
                  </div>
                  <button type="button" className={BUTTON} onClick={checkAgent}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Erneut prüfen
                  </button>
                </div>
                {!agentHealth && agentChecked ? (
                  <div className="mt-4">
                    <Hint>
                      Agent auf diesem Gerät starten: <span className="font-mono">python3 print-agent/fiscalpos_print_agent.py</span>
                      {' '}(Autostart auf macOS: <span className="font-mono">print-agent/install_macos.sh</span>). Chrome fragt beim ersten Mal nach Zugriff auf lokale Dienste. Bitte erlauben.
                    </Hint>
                  </div>
                ) : null}
              </DetailCard>
            ) : null}

            <DetailCard
              title="Eigene Zuordnung"
              subtitle="Nur für dieses Gerät, z. B. den mobilen Drucker eines Kellners. Küchenbons laufen immer über die Druckgruppen."
            >
              <div className="divide-y divide-slate-100 border border-border">
                {OVERRIDE_TARGETS.map((target) => (
                  <div key={target.id} className="grid gap-3 px-4 py-4 md:grid-cols-[minmax(0,1fr)_280px]">
                    <div>
                      <p className="text-base font-semibold text-foreground">{target.label}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{target.subtitle}</p>
                    </div>
                    <SelectField
                      value={overrides[target.id] || ''}
                      options={overrideOptions}
                      onChange={(value) => handleOverride(target.id, value)}
                      disabled={!isManager || busy === `override:${target.id}`}
                    />
                  </div>
                ))}
              </div>
            </DetailCard>

            {recentJobs.length > 0 ? (
              <DetailCard title="Zuletzt auf diesem Gerät gedruckt" compact>
                <div className="divide-y divide-slate-100">
                  {recentJobs.map((job) => (
                    <div key={`${job.id}-${job.at}`} className="flex items-center justify-between gap-3 py-3 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{job.title || 'Druckauftrag'} → {job.printerName}</p>
                        {job.error ? <p className="mt-1 text-red-600">{job.error}</p> : null}
                      </div>
                      <StatusBadge tone={job.status === 'done' ? 'positive' : 'danger'}>{job.status === 'done' ? 'Gedruckt' : 'Fehler'}</StatusBadge>
                    </div>
                  ))}
                </div>
              </DetailCard>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
